import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  LabelList,
  Cell,
  Tooltip,
} from 'recharts';
import { colors as colorsConst } from './constants';
import cn from 'classnames';
import { renderCustomizedLabel } from './helpers/renderCustomizedLabel';
import { getNewDomain } from './helpers/getNewDomain';
import styles from './HorizontalBar.module.scss';

const HorizontalBar = ({
  data = [],
  barSize = 12,
  colors = colorsConst,
  onItemClick,
  isActiveItem,
  sortDirection = 'asc',
  valueKey = 'value',
  nullContent,
}) => {
  const barplotContainerRef = useRef();
  const [domainMax, setDomainMax] = useState(0);

  const charHeight = useMemo(() => {
    const barGap = 17;
    return (barSize + barGap + 1) * data.length;
  }, [data, barSize]);

  useLayoutEffect(() => {
    if (barplotContainerRef.current && data.length) {
      const entity = sortDirection === 'asc' ? data[0] : data[data.length - 1];

      const newDomain = getNewDomain(
        barplotContainerRef.current,
        entity,
        valueKey
      );

      setDomainMax(newDomain);
    }
  }, [barplotContainerRef, data, sortDirection, valueKey]);

  const tooltip = (props) => {
    try {
      const { label, payload } = props;
      if (payload[0]) {
        const { payload: data } = payload[0];

        const value = data[valueKey];

        const val =
          value === 0 && nullContent !== undefined ? nullContent : value;

        return (
          <div className={styles.tooltip}>
            <div>{label}</div>
            <div className={styles.value}>{val}</div>
          </div>
        );
      } else return <div></div>;
    } catch (e) {
      return <div></div>;
    }
  };

  return (
    <div className={cn(styles.horizontalBar)} ref={barplotContainerRef}>
      <ResponsiveContainer width="100%" height={charHeight}>
        <BarChart
          layout={'vertical'}
          data={data}
          margin={{ top: 5, left: 5, right: 5, bottom: 5 }}
        >
          <XAxis type="number" domain={[0, domainMax]} hide />
          <YAxis type="category" dataKey="name" hide />
          <Tooltip cursor={{ fill: 'none' }} content={tooltip} />
          <Bar
            isAnimationActive={false}
            barSize={barSize}
            dataKey={valueKey}
            fill="#624AFA"
            radius={6}
          >
            {data.map((entry, index) => (
              <Cell
                key={entry.id || `cell-${index}`}
                fill={colors[index % colors.length]}
                className={styles.cell}
                onClick={
                  typeof onItemClick === 'function'
                    ? () => onItemClick(entry)
                    : undefined
                }
              />
            ))}
            <LabelList
              position="right"
              content={renderCustomizedLabel}
              valueAccessor={(props) => ({
                isActive:
                  typeof isActiveItem === 'function' &&
                  isActiveItem(props.payload),
                entity: props.payload,
                valueKey,
                nullContent,
              })}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default HorizontalBar;
